'use client';

/**
 * The consent surface mounted by the customer layouts (GitLab #649).
 *
 * One component so a layout wires consent in a single line and cannot mount
 * half of it: the provider that owns the state, the gate that asks, and the
 * GTM loader that acts on the answer. The footer's "Cookie settings" link
 * reads the same context, which is why `children` sit inside the provider.
 *
 * Suppression is decided here rather than by the layout because it depends
 * on the current path, and a layout does not re-render on client navigation
 * within its own subtree. `usePathname` does, so a customer who moves onto a
 * hand-off route is not asked mid-journey (§4.4).
 */
import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { ConsentProvider } from '@/components/analytics/ConsentProvider';
import { CookieConsentGate } from '@/components/analytics/CookieConsentGate';
import { GtmLoader } from '@/components/analytics/GtmLoader';
import { SentryReplayConsent } from '@/components/analytics/SentryReplayConsent';
import { isConsentGateSuppressed } from '@/lib/analytics/consentRoutes';
import { recordConsentChoice } from '@/lib/analytics/recordConsent';
import type { AnalyticsSettings, LegalContentSettings } from '@/types/organization';
export interface ConsentSurfaceProps {
  /** Resolved settings for the child org being browsed. */
  settings: AnalyticsSettings;
  /** Child org the choice is given under; recorded on the evidence row. */
  organizationId?: string;
  /** Per-request CSP nonce, stamped onto the GTM container script. */
  nonce?: string;
  /** Explicit suppression for flows that should never prompt in this view. */
  suppressGate?: boolean;
  /** The org's authored legal content, so the gate can show its cookie policy. */
  legalContent?: LegalContentSettings;
  children: ReactNode;
}
export function ConsentSurface(t0) {
  const $ = _c(17);
  const {
    settings,
    organizationId,
    nonce,
    suppressGate: suppressGateOverride,
    legalContent,
    children
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname || $[1] !== suppressGateOverride) {
    t1 = suppressGateOverride || isConsentGateSuppressed(pathname ?? "/");
    $[0] = pathname;
    $[1] = suppressGateOverride;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const suppressGate = t1;
  let t2;
  if ($[3] !== nonce || $[4] !== settings.gtm_container_id) {
    t2 = <GtmLoader containerId={settings.gtm_container_id} nonce={nonce} />;
    $[3] = nonce;
    $[4] = settings.gtm_container_id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SentryReplayConsent />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== legalContent || $[8] !== settings) {
    t4 = <CookieConsentGate settings={settings} legalContent={legalContent} />;
    $[7] = legalContent;
    $[8] = settings;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== organizationId || $[12] !== settings || $[13] !== suppressGate || $[14] !== t2 || $[15] !== t4) {
    t5 = <ConsentProvider settings={settings} organizationId={organizationId} suppressGate={suppressGate} onChoice={recordConsentChoice}>{children}{t2}{t3}{t4}</ConsentProvider>;
    $[10] = children;
    $[11] = organizationId;
    $[12] = settings;
    $[13] = suppressGate;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}